import React, { useState, useEffect, useRef } from "react";
import { Modal, Input, Alert, List, Typography, Space, Spin, Empty, Button, Tag } from 'antd';
import { PlusCircleOutlined, CheckCircleOutlined, WarningOutlined, HistoryOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as actions from "store/actions/AuthActions";

const { Text } = Typography;

const AssetEntryScanner = ({ visible, onClose }) => {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [scanValue, setScanValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [recentEntries, setRecentEntries] = useState([]);

    // Focus input when modal opens
    useEffect(() => {
        if (visible && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [visible]);

    // Load recent entries from localStorage
    useEffect(() => {
        if (visible) {
            const stored = localStorage.getItem('assetEntryHistory');
            if (stored) {
                try {
                    setRecentEntries(JSON.parse(stored));
                } catch (e) {
                    setRecentEntries([]);
                }
            }
        }
    }, [visible]);

    // Save recent entry to localStorage
    const saveRecentEntry = (barcode, asset, isNew) => {
        const entry = {
            barcode,
            asset_id: asset?.id,
            asset_name: asset?.name || 'New Asset',
            asset_number: asset?.asset_number || '-',
            isNew,
            timestamp: new Date().toISOString()
        };

        const updated = [entry, ...recentEntries.filter(e => e.barcode !== barcode)].slice(0, 10);
        setRecentEntries(updated);
        localStorage.setItem('assetEntryHistory', JSON.stringify(updated));
    };

    const handleScan = async (value) => {
        const barcode = value.trim();
        if (!barcode) return;

        setLoading(true);
        setResult(null);

        try {
            // Search for asset by barcode
            const response = await actions.loadAssets(1, barcode, 10, null);

            if (response && response.status === 200) {
                const assets = response.data.results || [];

                // Find exact barcode match
                const exactMatch = assets.find(a =>
                    a.barcode === barcode ||
                    a.asset_number === barcode
                );

                if (exactMatch) {
                    // Asset exists - show warning and option to view
                    setResult({
                        isNew: false,
                        asset: exactMatch,
                        message: 'Asset with this barcode already exists!'
                    });
                    saveRecentEntry(barcode, exactMatch, false);
                } else {
                    // Asset doesn't exist - navigate to create with barcode pre-filled
                    setResult({
                        isNew: true,
                        barcode: barcode,
                        message: 'Barcode not found. Creating new asset...'
                    });
                    saveRecentEntry(barcode, null, true);

                    // Navigate to create page with barcode in URL
                    setTimeout(() => {
                        onClose();
                        navigate(`/assets/new?barcode=${encodeURIComponent(barcode)}`);
                    }, 800);
                }
            } else {
                // API error - create new asset anyway
                setResult({
                    isNew: true,
                    barcode: barcode,
                    message: 'Creating new asset with scanned barcode...'
                });
                saveRecentEntry(barcode, null, true);

                setTimeout(() => {
                    onClose();
                    navigate(`/assets/new?barcode=${encodeURIComponent(barcode)}`);
                }, 800);
            }
        } catch (error) {
            console.error('Scan error:', error);
            // On error, still allow creating new asset
            setResult({
                isNew: true,
                barcode: barcode,
                message: 'Creating new asset with scanned barcode...'
            });
            saveRecentEntry(barcode, null, true);

            setTimeout(() => {
                onClose();
                navigate(`/assets/new?barcode=${encodeURIComponent(barcode)}`);
            }, 800);
        }

        setLoading(false);
        setScanValue('');

        // Refocus input for next scan
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleInputChange = (e) => {
        setScanValue(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleScan(scanValue);
        }
    };

    const handleRecentClick = (entry) => {
        if (!entry.isNew && entry.asset_id) {
            onClose();
            navigate(`/assets/edit/${entry.asset_id}`);
        }
    };

    const clearRecentEntries = () => {
        setRecentEntries([]);
        localStorage.removeItem('assetEntryHistory');
    };

    const handleClose = () => {
        setScanValue('');
        setResult(null);
        onClose();
    };

    const handleGoToExistingAsset = () => {
        if (result && result.asset) {
            onClose();
            navigate(`/assets/edit/${result.asset.id}`);
        }
    };

    const handleCreateAnyway = () => {
        if (result && result.asset) {
            onClose();
            navigate(`/assets/new?barcode=${encodeURIComponent(result.asset.barcode || scanValue)}`);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <PlusCircleOutlined style={{ color: '#52c41a' }} />
                    <span>Asset Entry Scanner</span>
                </Space>
            }
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={500}
            destroyOnClose
        >
            <Alert
                message="Scan barcode to create a new asset"
                description="The scanned barcode will be automatically filled in the asset form. If the barcode already exists, you'll be notified."
                type="success"
                showIcon
                icon={<PlusCircleOutlined />}
                style={{ marginBottom: 20 }}
            />

            <div style={{ marginBottom: 20 }}>
                <Input
                    ref={inputRef}
                    size="large"
                    placeholder="Scan barcode here..."
                    prefix={<PlusCircleOutlined style={{ color: '#52c41a' }} />}
                    value={scanValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    style={{ fontSize: 18, borderColor: '#52c41a' }}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Press Enter to process barcode
                </Text>
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 10 }}>
                        <Text>Checking barcode...</Text>
                    </div>
                </div>
            )}

            {result && !loading && (
                <Alert
                    message={result.isNew ? "Creating New Asset" : "Asset Already Exists"}
                    description={
                        result.isNew ? (
                            <div>
                                <Text>Barcode: <Text code>{result.barcode}</Text></Text>
                                <br />
                                <Text type="secondary">Redirecting to asset creation form...</Text>
                            </div>
                        ) : (
                            <div>
                                <Text strong>{result.asset.name}</Text>
                                <br />
                                <Text type="secondary">
                                    {result.asset.asset_number} | {result.asset.barcode}
                                </Text>
                                <div style={{ marginTop: 15 }}>
                                    <Space>
                                        <Button type="primary" onClick={handleGoToExistingAsset}>
                                            View Existing Asset
                                        </Button>
                                        <Button onClick={handleCreateAnyway}>
                                            Create New Anyway
                                        </Button>
                                    </Space>
                                </div>
                            </div>
                        )
                    }
                    type={result.isNew ? "success" : "warning"}
                    showIcon
                    icon={result.isNew ? <CheckCircleOutlined /> : <WarningOutlined />}
                    style={{ marginBottom: 20 }}
                />
            )}

            {recentEntries.length > 0 && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <Text strong>
                            <HistoryOutlined /> Recent Entries
                        </Text>
                        <Button type="link" size="small" onClick={clearRecentEntries}>
                            Clear
                        </Button>
                    </div>
                    <List
                        size="small"
                        bordered
                        dataSource={recentEntries}
                        renderItem={(entry) => (
                            <List.Item
                                style={{
                                    cursor: !entry.isNew ? 'pointer' : 'default',
                                    opacity: 1
                                }}
                                onClick={() => handleRecentClick(entry)}
                            >
                                <Space>
                                    {entry.isNew ? (
                                        <Tag color="green">NEW</Tag>
                                    ) : (
                                        <Tag color="orange">EXISTS</Tag>
                                    )}
                                    <div>
                                        <Text code>{entry.barcode}</Text>
                                        <Text type="secondary" style={{ marginLeft: 8 }}>
                                            {entry.asset_name}
                                            {!entry.isNew && ` (${entry.asset_number})`}
                                        </Text>
                                    </div>
                                </Space>
                            </List.Item>
                        )}
                        style={{ maxHeight: 200, overflowY: 'auto' }}
                    />
                </div>
            )}

            {recentEntries.length === 0 && !result && !loading && (
                <Empty
                    description="No recent entries"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            )}
        </Modal>
    );
};

export default AssetEntryScanner;
